import { AddOutlined, DeleteOutlined, LanOutlined, SettingsInputHdmiOutlined } from '@mui/icons-material'
import { Box, Button, Chip, Divider, ListItemIcon, Menu, MenuItem, Stack } from '@mui/material'
import type { GridColDef, GridRowId } from '@mui/x-data-grid'
import type { WritableDraft } from 'immer'
import type { MouseEvent } from 'react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { v4 as uuidV4 } from 'uuid'

import CustomDataGrid from '@/components/dataDisplay/CustomDataGrid'
import CustomTypography from '@/components/dataDisplay/CustomTypography'
import { BasicSerialPortSettingsDrawer } from '@/features/controller/configuration/components/drawers/BasicSerialPortSettingsDrawer'
import { ModbusTcpConnectionDrawer } from '@/features/controller/configuration/components/drawers/ModbusTcpConnectionDrawer'
import { NamedModbusSerialConnectionDrawer } from '@/features/controller/configuration/components/drawers/NamedModbusSerialConnectionDrawer'
import { NamedModbusTcpConnectionDrawer } from '@/features/controller/configuration/components/drawers/NamedModbusTcpConnectionDrawer'
import { SerialPortSettingDrawer } from '@/features/controller/configuration/components/drawers/SerialPortSettingsDrawer'
import { DeviceConfiguration } from '@/features/controller/configuration/components/tree/DeviceConfiguration'
import type { NodeActionsProps } from '@/features/controller/configuration/components/tree/NodeActions'
import { NodeActions, NodeMenuItemType } from '@/features/controller/configuration/components/tree/NodeActions'
import { NodeErrors } from '@/features/controller/configuration/components/tree/NodeErrors'
import { useDrawerContent } from '@/features/controller/configuration/hooks/useDrawerContent'
import { namedModbusConnectionId } from '@/features/controller/configuration/models/constants'
import type {
  AnyNamedModbusConnectionNode,
  BasicSerialConnectionNode,
  DeviceConnectionNode,
  LegacyModbusTcpConnectionNode,
  SerialAndModbusConfig,
} from '@/features/controller/configuration/models/controllerConfigTree'
import { DeviceConnectionNodeType } from '@/features/controller/configuration/models/controllerConfigTree'
import type {
  ControllerConfigurationModel,
  ControllerConfigurationModelUpdate,
} from '@/features/controller/configuration/models/controllerConfiguration'
import { SERIAL_PORT_CONFIG_DEFAULTS } from '@/features/controller/configuration/models/raspiProperties/defaults'
import { DEFAULT_MODBUS_TCP_PORT } from '@/features/controller/configuration/models/raspiProperties/namedModbusClients'
import { writeSerialMode } from '@/features/controller/configuration/models/raspiProperties/serialPort'
import type {
  LegacyModbusTcpConnection,
  NamedModbusClient,
  NamedModbusConnection,
  SerialPortConfig,
} from '@/features/controller/configuration/models/raspiProperties/types'
import { NamedModbusTransport, SerialPort } from '@/features/controller/configuration/models/raspiProperties/types'
import {
  getDeviceConnectionNodesFromRaspiProperties,
  makeLegacyModbusTcpConnectionNode,
  makeNamedModbusSerialConnectionNode,
  makeNamedModbusTcpConnectionNode,
} from '@/features/controller/configuration/utils/controllerConfigTreeMaker'
import { describeExternalModbusDevice } from '@/features/controller/configuration/utils/externalModbusDevices'
import { describeSerialPort } from '@/features/controller/configuration/utils/serialPorts'

interface DeviceConnectionsProps {
  configuration: ControllerConfigurationModel
  indent: boolean
  onConfigurationUpdated: (configurationUpdate: ControllerConfigurationModelUpdate) => void
}

const UPDATE_COMMENT = 'Update made in configuration UI'

function isNamedConnectionNode(node: DeviceConnectionNode): node is AnyNamedModbusConnectionNode {
  return (
    node.type === DeviceConnectionNodeType.NamedModbusTcp || node.type === DeviceConnectionNodeType.NamedModbusSerial
  )
}

/** A connection being edited, plus whether it is still being created (the name is then editable). */
interface EditableNamedModbus {
  isNew: boolean
  node: AnyNamedModbusConnectionNode
}

export function DeviceConnections({ configuration, indent, onConfigurationUpdated }: Readonly<DeviceConnectionsProps>) {
  const { t } = useTranslation()
  // A controller rarely has more than a couple of connections, so all start expanded. Tracking the
  // collapsed ones instead means a newly added connection opens too.
  const [collapsedRowIds, setCollapsedRowIds] = useState<Set<GridRowId>>(new Set())
  const basicSerialPortSettingsDrawer = useDrawerContent<BasicSerialConnectionNode['config']>()
  const serialPortSettingsDrawer = useDrawerContent<SerialAndModbusConfig>()
  const modbusTcpDrawer = useDrawerContent<LegacyModbusTcpConnectionNode>()
  const namedModbusDrawer = useDrawerContent<EditableNamedModbus>()
  const [addMenuAnchorEl, setAddMenuAnchorEl] = useState<HTMLElement | null>(null)

  const connections = useMemo(
    () =>
      configuration.raspiPropertiesConfig
        ? getDeviceConnectionNodesFromRaspiProperties(configuration.raspiPropertiesConfig, configuration.appConfig)
        : [],
    [configuration.raspiPropertiesConfig, configuration.appConfig],
  )

  const expandedRowIds = useMemo(
    () => new Set(connections.map((conn) => conn.id).filter((id) => !collapsedRowIds.has(id))),
    [connections, collapsedRowIds],
  )

  // The unmodelled clients count as taken too: their blocks are preserved, not ours to write into
  const takenConnectionNames = [
    ...connections.filter(isNamedConnectionNode).map((conn) => conn.config.namedClient.name),
    ...(configuration.raspiPropertiesConfig?.unrecognisedModbusClientNames ?? []),
  ]

  function renderSerialPortBaudRate({ baudRate }: SerialPortConfig) {
    return t('controllers.configuration.device_connections.serial.baud_rate_summary', { value: baudRate })
  }

  function renderNamedModbusClientProperties(client: NamedModbusClient) {
    const framing =
      client.transport === NamedModbusTransport.serial
        ? [renderSerialPortBaudRate(client.portConfig), writeSerialMode(client.portConfig)]
        : []
    return [
      ...framing,
      client.transport === NamedModbusTransport.tcp &&
        client.connectTimeoutMs !== undefined &&
        t('controllers.configuration.device_connections.named_modbus_tcp.connect_timeout_summary', {
          value: client.connectTimeoutMs,
        }),
      client.responseTimeoutMs !== undefined &&
        t('controllers.configuration.device_connections.named_modbus.response_timeout_summary', {
          value: client.responseTimeoutMs,
        }),
      client.retries !== undefined &&
        t('controllers.configuration.device_connections.named_modbus.retries_summary', {
          count: client.retries,
        }),
    ]
      .filter(Boolean)
      .join(', ')
  }

  /**
   * A named connection is only safe to remove once nothing points at it. Meters are visible here;
   * other device families are found through their read-only references.
   */
  function removeNamedConnectionDisabledReason(node: AnyNamedModbusConnectionNode) {
    const attachedMeters = node.config.meters.length
    if (attachedMeters > 0) {
      return t('controllers.configuration.device_connections.named_modbus.remove_blocked_by_meters', {
        count: attachedMeters,
      })
    }
    const externalReference = configuration.raspiPropertiesConfig?.externalModbusClientReferences.find(
      (reference) => reference.clientName === node.config.namedClient.name,
    )
    if (externalReference) {
      return t('controllers.configuration.device_connections.named_modbus.remove_blocked_by_device', {
        device: describeExternalModbusDevice(externalReference, t),
      })
    }
    return undefined
  }

  /**
   * Chosen for what the glyph looks like at 24px, not for what MUI calls it. `SettingsInputHdmi` is a
   * keyed connector housing, which is close to the D-sub shape of a serial port; `Lan` is a local
   * network, which is what Modbus/TCP runs over in practice rather than the internet. MUI has no
   * RJ45 or RS-485 glyph, and the brand library's are device-level line art that turns to mush at
   * this size.
   */
  function renderTransportIcon(row: DeviceConnectionNode) {
    switch (row.type) {
      case DeviceConnectionNodeType.BasicSerial:
      case DeviceConnectionNodeType.LegacySerial:
      case DeviceConnectionNodeType.NamedModbusSerial:
        return (
          <SettingsInputHdmiOutlined
            titleAccess={t('controllers.configuration.device_connections.serial.icon_title')}
          />
        )
      case DeviceConnectionNodeType.LegacyModbusTcp:
      case DeviceConnectionNodeType.NamedModbusTcp:
        return <LanOutlined titleAccess={t('controllers.configuration.device_connections.modbus_tcp.icon_title')} />
    }
  }

  function nodeActionsProps(row: DeviceConnectionNode): NodeActionsProps {
    switch (row.type) {
      case DeviceConnectionNodeType.BasicSerial:
        return {
          editAction: () => basicSerialPortSettingsDrawer.show(row.config),
          editTitle: t('controllers.configuration.device_connections.basic_serial.edit'),
        }
      case DeviceConnectionNodeType.LegacySerial:
        return {
          editAction: () => serialPortSettingsDrawer.show(row.config),
          editTitle: t('controllers.configuration.device_connections.serial.edit'),
        }
      case DeviceConnectionNodeType.LegacyModbusTcp:
        return {
          editAction: () => modbusTcpDrawer.show(row),
          editTitle: t('controllers.configuration.device_connections.modbus_tcp.edit'),
        }
      case DeviceConnectionNodeType.NamedModbusTcp:
      case DeviceConnectionNodeType.NamedModbusSerial:
        return {
          editAction: () => namedModbusDrawer.show({ isNew: false, node: row }),
          editTitle:
            row.type === DeviceConnectionNodeType.NamedModbusSerial
              ? t('controllers.configuration.device_connections.named_modbus_serial.edit')
              : t('controllers.configuration.device_connections.named_modbus_tcp.edit'),
          menuActions: [
            {
              disabledReason: removeNamedConnectionDisabledReason(row),
              icon: <DeleteOutlined />,
              label: t('controllers.configuration.device_connections.named_modbus.remove'),
              perform: async () => removeNamedModbusConnection(row),
              type: NodeMenuItemType.action,
            },
          ],
        }
    }
  }

  const columns: GridColDef<DeviceConnectionNode>[] = [
    {
      field: 'label',
      flex: 1,
      renderCell: ({ row, value }) => (
        <Stack alignItems="center" direction="row" gap={1}>
          {renderTransportIcon(row)}
          {value}
        </Stack>
      ),
    },
    {
      field: 'target',
      flex: 1,
      valueGetter: (_, v: DeviceConnectionNode): string => {
        switch (v.type) {
          case DeviceConnectionNodeType.BasicSerial:
            return describeSerialPort(v.config.basicDevicesSerialPort, t)
          case DeviceConnectionNodeType.LegacySerial:
            return describeSerialPort(v.config.legacySerialPortConfig.serialPort, t)
          case DeviceConnectionNodeType.NamedModbusSerial:
            return describeSerialPort(v.config.namedClient.serialPort, t)
          case DeviceConnectionNodeType.LegacyModbusTcp: {
            const { host, port } = v.config.tcpTarget
            return `${host}:${port}`
          }
          case DeviceConnectionNodeType.NamedModbusTcp: {
            const { address, port } = v.config.namedClient
            return `${address}:${port}`
          }
        }
      },
    },
    {
      field: 'properties',
      flex: 3,
      renderCell: ({ value }) => (
        <Stack alignItems="center" direction="row">
          {value}
        </Stack>
      ),
      valueGetter: (_, v: DeviceConnectionNode): string => {
        switch (v.type) {
          case DeviceConnectionNodeType.BasicSerial:
            return t('controllers.configuration.device_connections.basic_serial.properties_summary')
          case DeviceConnectionNodeType.LegacySerial: {
            const c = v.config
            return [
              renderSerialPortBaudRate(c.legacySerialPortConfig.portConfig),
              writeSerialMode(c.legacySerialPortConfig.portConfig),
            ]
              .filter(Boolean)
              .join(', ')
          }
          case DeviceConnectionNodeType.LegacyModbusTcp:
            // Legacy TCP has no connection-level settings: the controller reads timeouts only from
            // controller.modbus.clients, so a value here would be written to a key nothing reads.
            return ''
          case DeviceConnectionNodeType.NamedModbusTcp:
          case DeviceConnectionNodeType.NamedModbusSerial:
            return renderNamedModbusClientProperties(v.config.namedClient)
        }
      },
    },
    {
      field: 'errors',
      minWidth: 58,
      maxWidth: 58,
      renderCell: ({ row }) => <NodeErrors errors={row.errors} />,
    },
    {
      field: 'actions',
      // We can't allow flex width as that could hide the important buttons
      maxWidth: 102,
      minWidth: 102,
      renderCell: ({ row }) => <NodeActions {...nodeActionsProps(row)} />,
    },
  ]

  function onModbusTcpUpdated(modbusTcpConnection: LegacyModbusTcpConnection) {
    onConfigurationUpdated({
      updateRaspiPropertiesConfig: (draft) => {
        const found = draft.legacyModbusConnections.find((conn) => conn.id === modbusTcpConnection.id)
        if (found) {
          const draftLegacyModbusTcpConnection = found as WritableDraft<LegacyModbusTcpConnection>
          draftLegacyModbusTcpConnection.tcpTarget = modbusTcpConnection.tcpTarget
        } else {
          draft.legacyModbusConnections.push(modbusTcpConnection)
        }
      },
      comment: UPDATE_COMMENT,
    })
    modbusTcpDrawer.close()
  }

  function updateBasicSerialPortSettings({ basicDevicesSerialPort }: BasicSerialConnectionNode['config']) {
    onConfigurationUpdated({
      updateRaspiPropertiesConfig: (draft) => {
        draft.basicDevicesSerialPort = basicDevicesSerialPort
      },
      comment: UPDATE_COMMENT,
    })
    basicSerialPortSettingsDrawer.close()
  }

  function updateSerialPortSettings(updatedConfig: SerialAndModbusConfig) {
    onConfigurationUpdated({
      updateRaspiPropertiesConfig: (draft) => {
        draft.legacySerialPortConfig = updatedConfig.legacySerialPortConfig
        draft.legacyModbusSerialConfig = updatedConfig.legacyModbusSerialConfig
      },
      comment: UPDATE_COMMENT,
    })
    serialPortSettingsDrawer.close()
  }

  function onNamedModbusUpdated(connection: NamedModbusConnection) {
    // The id follows the name, which is fixed once the connection exists
    const updated = { ...connection, id: namedModbusConnectionId(connection.namedClient.name) }
    onConfigurationUpdated({
      updateRaspiPropertiesConfig: (draft) => {
        const found = draft.legacyModbusConnections.find((conn) => conn.id === updated.id)
        if (found) {
          const draftNamedConnection = found as WritableDraft<NamedModbusConnection>
          draftNamedConnection.namedClient = updated.namedClient
        } else {
          draft.legacyModbusConnections.push(updated)
        }
      },
      comment: UPDATE_COMMENT,
    })
    namedModbusDrawer.close()
  }

  function removeNamedModbusConnection(node: AnyNamedModbusConnectionNode) {
    onConfigurationUpdated({
      updateRaspiPropertiesConfig: (draft) => {
        const index = draft.legacyModbusConnections.findIndex((conn) => conn.id === node.id)
        if (index > -1) {
          draft.legacyModbusConnections.splice(index, 1)
        }
      },
      comment: UPDATE_COMMENT,
    })
  }

  function addLegacyModbusTcpConnection() {
    const newId = 'modbus-tcp-' + uuidV4()
    const newNode = makeLegacyModbusTcpConnectionNode({
      id: newId,
      errors: [],
      meters: [],
      tcpTarget: {
        host: '',
        port: DEFAULT_MODBUS_TCP_PORT,
      },
    })
    modbusTcpDrawer.show(newNode)
  }

  function addNamedModbusTcpConnection() {
    const newNode = makeNamedModbusTcpConnectionNode({
      id: namedModbusConnectionId(''),
      errors: [],
      meters: [],
      namedClient: {
        address: '',
        name: '',
        port: DEFAULT_MODBUS_TCP_PORT,
        transport: NamedModbusTransport.tcp,
      },
    })
    namedModbusDrawer.show({ isNew: true, node: newNode })
  }

  function addNamedModbusSerialConnection() {
    const newNode = makeNamedModbusSerialConnectionNode({
      id: namedModbusConnectionId(''),
      errors: [],
      meters: [],
      namedClient: {
        name: '',
        portConfig: SERIAL_PORT_CONFIG_DEFAULTS,
        serialPort: SerialPort.port1,
        transport: NamedModbusTransport.serial,
      },
    })
    namedModbusDrawer.show({ isNew: true, node: newNode })
  }

  const editedNamedModbus = namedModbusDrawer.showing?.content

  return (
    <Box ml={indent ? 6 : 0}>
      <CustomDataGrid
        columnHeaderHeight={0}
        columns={columns}
        detailPanelExpandedRowIds={expandedRowIds}
        disableColumnReorder={true}
        disableColumnSorting={true}
        getDetailPanelContent={({ row }) => (
          <DeviceConfiguration
            configuration={configuration}
            connectionNode={row}
            onConfigurationUpdated={onConfigurationUpdated}
          />
        )}
        getDetailPanelHeight={() => 'auto'}
        getRowId={(row: DeviceConnectionNode) => row.id}
        hideFooter={true}
        includeWrapper={false}
        rows={connections}
        onDetailPanelExpandedRowIdsChange={(ids) =>
          setCollapsedRowIds(new Set(connections.map((conn) => conn.id).filter((id) => !ids.has(id))))
        }
      />
      <Stack direction="row">
        <Button onClick={(event: MouseEvent<HTMLElement>) => setAddMenuAnchorEl(event.currentTarget)}>
          <Chip
            color="default"
            icon={<AddOutlined />}
            label={t('controllers.configuration.device_connections.add')}
            variant="filled"
          />
        </Button>
        <Menu anchorEl={addMenuAnchorEl} open={Boolean(addMenuAnchorEl)} onClose={() => setAddMenuAnchorEl(null)}>
          <MenuItem
            onClick={() => {
              setAddMenuAnchorEl(null)
              addNamedModbusTcpConnection()
            }}
          >
            {/* The label sits beside the icon, so it is decorative and takes no titleAccess */}
            <ListItemIcon>
              <LanOutlined />
            </ListItemIcon>
            {t('controllers.configuration.device_connections.named_modbus_tcp.add')}
          </MenuItem>
          <MenuItem
            onClick={() => {
              setAddMenuAnchorEl(null)
              addNamedModbusSerialConnection()
            }}
          >
            <ListItemIcon>
              <SettingsInputHdmiOutlined />
            </ListItemIcon>
            {t('controllers.configuration.device_connections.named_modbus_serial.add')}
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={() => {
              setAddMenuAnchorEl(null)
              addLegacyModbusTcpConnection()
            }}
          >
            <ListItemIcon>
              <LanOutlined />
            </ListItemIcon>
            <Stack direction="column">
              {t('controllers.configuration.device_connections.legacy_modbus_tcp.add')}
              <CustomTypography color="text.secondary" variant="body2">
                {t('controllers.configuration.device_connections.legacy_modbus_tcp.add_hint')}
              </CustomTypography>
            </Stack>
          </MenuItem>
        </Menu>
      </Stack>
      {modbusTcpDrawer.showing && (
        <ModbusTcpConnectionDrawer
          config={modbusTcpDrawer.showing.content.config}
          open={modbusTcpDrawer.isOpen}
          onClose={modbusTcpDrawer.close}
          onConfigUpdated={onModbusTcpUpdated}
          onFinishedClosing={modbusTcpDrawer.forgetContent}
        />
      )}
      {editedNamedModbus?.node.type === DeviceConnectionNodeType.NamedModbusTcp && (
        <NamedModbusTcpConnectionDrawer
          config={editedNamedModbus.node.config}
          isNewConnection={editedNamedModbus.isNew}
          open={namedModbusDrawer.isOpen}
          takenNames={editedNamedModbus.isNew ? takenConnectionNames : []}
          onClose={namedModbusDrawer.close}
          onConfigUpdated={onNamedModbusUpdated}
          onFinishedClosing={namedModbusDrawer.forgetContent}
        />
      )}
      {editedNamedModbus?.node.type === DeviceConnectionNodeType.NamedModbusSerial && (
        <NamedModbusSerialConnectionDrawer
          config={editedNamedModbus.node.config}
          isNewConnection={editedNamedModbus.isNew}
          open={namedModbusDrawer.isOpen}
          takenNames={editedNamedModbus.isNew ? takenConnectionNames : []}
          onClose={namedModbusDrawer.close}
          onConfigUpdated={onNamedModbusUpdated}
          onFinishedClosing={namedModbusDrawer.forgetContent}
        />
      )}
      {basicSerialPortSettingsDrawer.showing && (
        <BasicSerialPortSettingsDrawer
          config={basicSerialPortSettingsDrawer.showing.content}
          open={basicSerialPortSettingsDrawer.isOpen}
          onClose={basicSerialPortSettingsDrawer.close}
          onConfigUpdated={updateBasicSerialPortSettings}
          onFinishedClosing={basicSerialPortSettingsDrawer.forgetContent}
        />
      )}
      {serialPortSettingsDrawer.showing && (
        <SerialPortSettingDrawer
          config={serialPortSettingsDrawer.showing.content}
          open={serialPortSettingsDrawer.isOpen}
          onClose={serialPortSettingsDrawer.close}
          onConfigUpdated={updateSerialPortSettings}
          onFinishedClosing={serialPortSettingsDrawer.forgetContent}
        />
      )}
    </Box>
  )
}
